import type { BoardDoc } from '../doc/BoardDoc';
import type { Point } from '../doc/geometry';
import { DEFAULT_BOARDCARD_SIZE, type BoardCardItem } from '../doc/schema';
import type { BoardSummary } from '../sync/boardsApi';
import { navigate, paths } from '../app/router';
import type { Ctx } from './actions';
import { fetchBoardLookup, primeBoardLookup } from './items/boardSummary';
import { flushThumbnails } from './thumbnail';

/** Creates a card pointing at another board, centred on a world point, and selects it. */
export function createBoardCardAt(ctx: Ctx, target: BoardSummary | string, world: Point): string {
  const { w, h } = DEFAULT_BOARDCARD_SIZE;
  const boardId = typeof target === 'string' ? target : target.id;
  if (typeof target !== 'string') primeBoardLookup(target);
  const id = ctx.board.createItem({
    type: 'boardcard',
    x: Math.round(world.x - w / 2),
    y: Math.round(world.y - h / 2),
    w,
    h,
    props: { boardId, title: '', description: '', coverAssetId: null, showPreview: true, layout: 'card', fit: 'cover', color: null },
  });
  ctx.pane.getState().select([id]);
  return id;
}

/** Board id when the text is a link to a board on this very app (pasting one makes a board card). */
export function boardIdFromUrl(text: string): string | null {
  const t = text.trim();
  if (!/^https?:\/\//i.test(t)) return null;
  try {
    const u = new URL(t);
    if (u.origin !== window.location.origin || u.pathname !== window.location.pathname) return null;
    const m = /^#\/b\/([A-Za-z0-9_-]+)/.exec(u.hash);
    return m ? m[1]! : null;
  } catch {
    return null;
  }
}

/**
 * Opens the board a card points at, in place of the current board or next to it (split view).
 * Nothing happens for a card of the board itself or of a board the visitor cannot see. The
 * current board's thumbnail is pushed first, like the toolbar's home button does.
 */
export async function openBoardCard(current: BoardDoc, item: BoardCardItem, split = false): Promise<void> {
  const target = item.props.boardId;
  if (!target || target === current.boardId || !current.remote) return;
  const lookup = await fetchBoardLookup(target);
  if (lookup.status === 'missing') return;
  if (split) {
    navigate(paths.board(current.boardId, target));
    return;
  }
  await flushThumbnails();
  navigate(paths.board(target));
}

// The board picker panel serves two callers: the toolbar (new card) and the inspector (re-point
// an existing card). The inspector leaves the card's id here before opening the panel.
let pickerTarget: string | null = null;

export function setBoardPickerTarget(itemId: string | null): void {
  pickerTarget = itemId;
}

export function takeBoardPickerTarget(): string | null {
  const t = pickerTarget;
  pickerTarget = null;
  return t;
}
